import { useEffect } from 'react';
import { isOfferTypeValue } from '../../../../../store/offer-store';
import { updateOfferInlinePrice } from '../../../services';
import { offerInlineEditStrategies } from '../offerInlineEditStrategies';
import type { IRowData } from '../OfferTable';
import { useOfferInlineEditStore } from './useOfferInlineEditStore';

export const useOfferInlineEdit = (rowData: IRowData, campaignId: string) => {
  const productId = rowData.product_uuid;
  const { initRow, setPrice, setDiscount, commitRow, rollbackRow } = useOfferInlineEditStore();

  const localPrice = useOfferInlineEditStore(
    (state) => state.rows[productId]?.localPrice ?? rowData.offer_final_price ?? null,
  );
  const localDiscount = useOfferInlineEditStore(
    (state) => state.rows[productId]?.localDiscount ?? rowData.offer_discount_percentage ?? null,
  );
  const originalPrice = useOfferInlineEditStore(
    (state) => state.rows[productId]?.originalPrice ?? rowData.offer_final_price ?? null,
  );
  const originalDiscount = useOfferInlineEditStore(
    (state) => state.rows[productId]?.originalDiscount ?? rowData.offer_discount_percentage ?? null,
  );

  useEffect(() => {
    initRow(
      productId,
      rowData.offer_final_price ?? null,
      rowData.offer_discount_percentage ?? null,
      rowData.offer_group_id ?? null,
      rowData.offer_group ?? null,
    );
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPriceChange = (value: number | null) => {
    setPrice(productId, value);
    if (value !== null) {
      const offerType = rowData.offer_dto?.offer?.offer_type;
      if (isOfferTypeValue(offerType)) {
        const computed = offerInlineEditStrategies[offerType].priceToDiscount(value, rowData);
        if (computed !== null) {
          setDiscount(productId, computed);
        }
      }
    }
  };

  const onDiscountChange = (value: number | null) => {
    setDiscount(productId, value);
    if (value !== null) {
      const offerType = rowData.offer_dto?.offer?.offer_type;
      if (isOfferTypeValue(offerType)) {
        const computed = offerInlineEditStrategies[offerType].discountToPrice(value, rowData);
        if (computed !== null) {
          setPrice(productId, computed);
        }
      }
    }
  };

  const buildSaveRequest = () => {
    const rowState = useOfferInlineEditStore.getState().rows[productId];
    const offerGroupId = rowState?.localOfferGroupId ?? rowData.offer_group_id;
    const offerGroupName = rowState?.localOfferGroupName ?? rowData.offer_group;

    return {
      payload: {
        product_id: productId,
        new_offer_price: localPrice,
        new_offer_discount: localDiscount,
        new_offer_group_id: offerGroupId,
      },
      commitOptions: {
        offerGroupId,
        offerGroupName,
        offerGroupRevision: rowState?.offerGroupRevision ?? 0,
      },
    };
  };

  const onPriceBlur = () => {
    if (campaignId && localPrice !== originalPrice) {
      const { payload, commitOptions } = buildSaveRequest();
      updateOfferInlinePrice(campaignId, payload)
        .then(() => commitRow(productId, commitOptions))
        .catch(() => rollbackRow(productId));
    }
  };

  const onDiscountBlur = () => {
    if (campaignId && localDiscount !== originalDiscount) {
      const { payload, commitOptions } = buildSaveRequest();
      updateOfferInlinePrice(campaignId, payload)
        .then(() => commitRow(productId, commitOptions))
        .catch(() => rollbackRow(productId));
    }
  };

  return {
    localPrice,
    localDiscount,
    onPriceChange,
    onDiscountChange,
    onPriceBlur,
    onDiscountBlur,
  };
};
